import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Pencil, LogOut, ExternalLink } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TESTIDS } from "@/constants/testIds";
import { ASSETS } from "@/constants/site";

const emptyForm = { id: null, title: "", excerpt: "", content: "", cover_image: "", author: "Equipe Visiovet", tags: "", published: true };

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openEditor, setOpenEditor] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [messages, setMessages] = useState([]);
  const [tab, setTab] = useState("posts");

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, mRes] = await Promise.all([
        api.get("/admin/blog/posts"),
        api.get("/admin/contact-messages"),
      ]);
      setPosts(pRes.data);
      setMessages(mRes.data);
    } catch (e) {
      toast.error("Falha ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyForm); setOpenEditor(true); };
  const openEdit = (p) => {
    setForm({
      id: p.id, title: p.title, excerpt: p.excerpt, content: p.content,
      cover_image: p.cover_image || "", author: p.author,
      tags: (p.tags || []).join(", "), published: p.published,
    });
    setOpenEditor(true);
  };

  const save = async () => {
    if (!form.title || !form.excerpt || !form.content) {
      toast.error("Preencha título, resumo e conteúdo");
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title,
      excerpt: form.excerpt,
      content: form.content,
      cover_image: form.cover_image || null,
      author: form.author || "Equipe Visiovet",
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      published: !!form.published,
    };
    try {
      if (form.id) {
        await api.put(`/admin/blog/posts/${form.id}`, payload);
        toast.success("Post atualizado");
      } else {
        await api.post("/admin/blog/posts", payload);
        toast.success("Post criado");
      }
      setOpenEditor(false);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/blog/posts/${id}`);
      toast.success("Post excluído");
      load();
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  const doLogout = async () => { await logout(); nav("/admin/login"); };

  return (
    <div className="min-h-screen bg-slate-50" data-testid={TESTIDS.adminDashboard}>
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={ASSETS.logo} alt="Visiovet" className="h-9 w-auto" />
            <div className="hidden sm:block leading-tight border-l border-slate-200 pl-3 ml-1">
              <div className="text-xs font-semibold text-slate-700">Painel administrativo</div>
              <div className="text-[10px] text-slate-500">{user?.email}</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/blog" target="_blank" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-brand-600">
              Ver blog <ExternalLink className="w-3.5 h-3.5" />
            </Link>
            <button
              onClick={doLogout}
              data-testid={TESTIDS.adminLogout}
              className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-red-600"
            >
              <LogOut className="w-4 h-4" /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Painel administrativo</h1>
            <p className="text-slate-600 text-sm mt-1">Gerencie posts do blog e visualize mensagens de contato.</p>
          </div>
          <div className="inline-flex bg-white border border-slate-200 rounded-full p-1">
            <button onClick={() => setTab("posts")} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${tab === "posts" ? "bg-brand-500 text-white" : "text-slate-600"}`}>Posts do blog</button>
            <button onClick={() => setTab("messages")} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${tab === "messages" ? "bg-brand-500 text-white" : "text-slate-600"}`}>Mensagens</button>
          </div>
        </div>

        {tab === "posts" && (
          <>
            <div className="flex justify-end mb-4">
              <Dialog open={openEditor} onOpenChange={setOpenEditor}>
                <DialogTrigger asChild>
                  <button
                    data-testid={TESTIDS.adminNewPost}
                    onClick={openNew}
                    className="inline-flex items-center gap-2 rounded-full bg-brand-500 text-white font-semibold px-5 py-2.5 shadow-lg shadow-brand-500/30 hover:bg-brand-600 transition-colors"
                  >
                    <Plus className="w-4 h-4" /> Novo post
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>{form.id ? "Editar post" : "Novo post"}</DialogTitle>
                    <DialogDescription>Preencha os campos abaixo. O conteúdo aceita formatação Markdown.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-2">
                    <div>
                      <Label>Título *</Label>
                      <Input className="mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                    </div>
                    <div>
                      <Label>Resumo *</Label>
                      <Textarea className="mt-1.5" rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
                    </div>
                    <div>
                      <Label>Conteúdo (Markdown) *</Label>
                      <Textarea className="mt-1.5 font-mono text-sm" rows={12} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label>URL da capa</Label>
                        <Input className="mt-1.5" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} placeholder="https://..." />
                      </div>
                      <div>
                        <Label>Autor</Label>
                        <Input className="mt-1.5" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} />
                      </div>
                    </div>
                    <div>
                      <Label>Tags (separadas por vírgula)</Label>
                      <Input className="mt-1.5" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="ressonância, neurologia" />
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} />
                      <span className="text-sm text-slate-700">Publicado</span>
                    </div>
                  </div>
                  <DialogFooter>
                    <button
                      onClick={save}
                      disabled={saving}
                      data-testid={TESTIDS.adminSavePost}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-500 text-white font-semibold px-5 py-2 hover:bg-brand-600 disabled:opacity-60"
                    >
                      {saving && <Loader2 className="w-4 h-4 animate-spin" />} Salvar
                    </button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Título</TableHead>
                    <TableHead className="hidden md:table-cell">Autor</TableHead>
                    <TableHead className="hidden md:table-cell">Data</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-10">Carregando...</TableCell></TableRow>
                  ) : posts.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-10">Nenhum post ainda.</TableCell></TableRow>
                  ) : posts.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium text-slate-900">{p.title}</TableCell>
                      <TableCell className="hidden md:table-cell text-slate-600">{p.author}</TableCell>
                      <TableCell className="hidden md:table-cell text-slate-600 text-sm">{new Date(p.created_at).toLocaleDateString("pt-BR")}</TableCell>
                      <TableCell>
                        <span className={`text-xs px-2.5 py-1 rounded-full ${p.published ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-600"}`}>
                          {p.published ? "Publicado" : "Rascunho"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => openEdit(p)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-600" data-testid={`edit-post-${p.id}`}>
                            <Pencil className="w-4 h-4" />
                          </button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button className="p-2 rounded-lg hover:bg-red-50 text-red-600" data-testid={`delete-post-${p.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Excluir post?</AlertDialogTitle>
                                <AlertDialogDescription>Esta ação não pode ser desfeita. O post "{p.title}" será removido permanentemente.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction onClick={() => remove(p.id)}>Excluir</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {tab === "messages" && (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead className="hidden md:table-cell">E-mail</TableHead>
                  <TableHead className="hidden md:table-cell">Telefone</TableHead>
                  <TableHead>Mensagem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {messages.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-10">Nenhuma mensagem ainda.</TableCell></TableRow>
                ) : messages.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell className="text-sm">{new Date(m.created_at).toLocaleString("pt-BR")}</TableCell>
                    <TableCell className="font-medium">{m.name}</TableCell>
                    <TableCell className="hidden md:table-cell text-sm">{m.email}</TableCell>
                    <TableCell className="hidden md:table-cell text-sm">{m.phone}</TableCell>
                    <TableCell className="max-w-md text-sm text-slate-600 truncate" title={m.message}>{m.message}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </main>
    </div>
  );
}
